import { memo, useState, useEffect, type FC } from 'react';
import { useTranslation } from 'react-i18next';
//Import Components
import DataTable from 'components/common/DataTable';
// Import Services
import { CustomerJourneyHttpService } from 'services/http';
//Import Config
import { TimeLineDataTableColumnsConfigs } from './config';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
// Import Version History
import { useVersionHistoryContext } from 'components/VersionHistory';
//Import SCSS
import 'assets/scss/segmentModal.scss';

interface Props {
    workflowId: number;
}

const ActivityTimeLine: FC<Props> = ({ workflowId }) => {
    const { t } = useTranslation();
    const { doGetRequest } = useRequest();
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState<AnyObject[]>([]);

    const { pointInTime } = useVersionHistoryContext();

    const { columns, defaultSorting } = TimeLineDataTableColumnsConfigs(t);
    const getTableRows = () => {
        return data.map(({ Date, UserName, Type, Action, Source, Name, Info, SystemMessage }, index) => {
            return {
                hasHover: true,
                dragDisable: true,
                data: {
                    index,
                    isRowOpend: false,
                    Date,
                    UserName,
                    Type,
                    Name,
                    Action: Action.Name,
                    Source: Source.Name,
                    Info,
                    SystemMessage,
                },
            };
        });
    };
    const getTableColumns = () => {
        return columns;
    };

    const { getCustomerJourneyTimeline } = useCancelableRequests(
        { getCustomerJourneyTimeline: CustomerJourneyHttpService.getCustomerJourneyTimeline },
        { takeLatest: ['getCustomerJourneyTimeline'] },
    );

    const getTimeLineData = () => {
        setIsLoading(true);
        doGetRequest(getCustomerJourneyTimeline.request, {
            queryString: {
                workflowId,
                pointInTime,
            },
            successCallback: (data) => {
                setData(data);
            },
        }).then(({ IsCanceled }) => {
            // A superseded call must not hide the loader of the call that replaced it
            if (!IsCanceled) {
                setIsLoading(false);
            }
        });
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(getTimeLineData, [pointInTime]);

    return (
        <DataTable
            classNames="full-height crm-activity-timeline-table"
            rowKey="index"
            columnKey="dataKey"
            withPagination={false}
            withSearch={false}
            withPageSelector={false}
            data={getTableRows()}
            dataCount={data?.length}
            columns={getTableColumns()}
            isLoading={isLoading}
            sorting={defaultSorting}
        />
    );
};

export default memo(ActivityTimeLine);
